import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { reactGaEvent } from '../utils/cookie-consent/react-ga-safe';
import { CaretRight } from '@phosphor-icons/react';
import { Link } from './Link';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  onClick?: () => void;
}

export interface BreadcrumbNavigationProps {
  items: BreadcrumbItem[];
  className?: string;
  showHome?: boolean;
  trackHomeClick?: boolean;
  trackData?: Record<string, any>;
  useChevronRightIcon?: boolean;
  linkClassName?: string;
  chevronClassName?: string;
  /** Light text on dark hero imagery (overrides default earth-tone breadcrumb colours). */
  variant?: 'default' | 'heroDark';
}

/**
 * Shared BreadcrumbNavigation component for use across all pages
 * Supports multiple breadcrumb items with optional links and click handlers
 */
const BREADCRUMB_BASE_CLASSES =
  'flex items-center flex-wrap gap-1 text-sm text-[#7A6E5F] dark:text-[#B8A48C]';

const HERO_DARK_NAV_CLASSES =
  'flex items-center flex-wrap gap-1 text-xs sm:text-sm text-white/95 dark:text-white/95 drop-shadow-sm';

export const BreadcrumbNavigation: React.FC<BreadcrumbNavigationProps> = ({
  items,
  className,
  showHome = true,
  trackHomeClick = false,
  trackData,
  useChevronRightIcon = false,
  linkClassName,
  chevronClassName,
  variant = 'default',
}) => {
  const intl = useIntl();
  const ChevronIcon = CaretRight;
  const resolvedClassName =
    variant === 'heroDark'
      ? [HERO_DARK_NAV_CLASSES, className ?? 'mb-0 px-0'].filter(Boolean).join(' ')
      : [BREADCRUMB_BASE_CLASSES, className ?? 'mb-4 px-1 sm:px-0'].filter(Boolean).join(' ');

  const handleHomeClick = () => {
    if (trackHomeClick) {
      const isDebugEnabled = typeof window !== 'undefined' && 
        (localStorage.getItem('__DEBUG_RENDER__') === 'true' || 
         new URLSearchParams(window.location.search).has('debug-render'));
      
      if (isDebugEnabled) {
        console.log('[NAV] Breadcrumb home clicked', {
          ...trackData,
          timestamp: new Date().toISOString(),
        });
      }
      
      reactGaEvent({
        category: 'interactions',
        action: 'click_breadcrumb_home',
        label: typeof window !== 'undefined' ? window.location.pathname : undefined,
        nonInteraction: false,
        ...trackData,
      });
    }
  };

  const allItems: BreadcrumbItem[] = showHome
    ? [
        {
          label: intl.formatMessage({
            id: 'navigation.home.breadcrumb',
            defaultMessage: 'Home',
            description: 'Breadcrumb link to home page',
          }),
          href: '/',
          onClick: handleHomeClick,
        },
        ...items,
      ]
    : items;

  return (
    <nav 
      className={resolvedClassName}
      aria-label={intl.formatMessage({ 
        id: 'breadcrumb.navigation', 
        defaultMessage: 'Breadcrumb navigation', 
        description: 'Accessibility label for breadcrumb navigation showing page hierarchy' 
      })}
    >
      {allItems.map((item, index) => {
        const isLast = index === allItems.length - 1;

        const defaultLinkClassName =
          variant === 'heroDark'
            ? 'text-white/95 hover:text-white dark:text-white/95 dark:hover:text-white transition-colors duration-200'
            : 'text-[#7A6E5F] dark:text-[#B8A48C] hover:text-[#3B5E45] dark:hover:text-[#98CFA8] transition-colors duration-200';
        const defaultChevronClassName =
          variant === 'heroDark'
            ? useChevronRightIcon
              ? 'h-3 w-3 sm:h-4 sm:w-4 mx-1 sm:mx-2 text-white/75 dark:text-white/75'
              : 'mx-2 text-white/75 dark:text-white/75'
            : useChevronRightIcon
              ? 'h-4 w-4 mx-1 sm:mx-2 text-[#7A6E5F] dark:text-[#8A7060]'
              : 'mx-2 text-[#7A6E5F] dark:text-[#8A7060]';

        const lastSpanClassName =
          variant === 'heroDark'
            ? 'text-white font-semibold drop-shadow-sm dark:text-white'
            : 'text-[#1E1A14] dark:text-[#F2EADF] font-medium';
        const midSpanClassName =
          variant === 'heroDark'
            ? 'text-white/90 dark:text-white/90'
            : 'text-[#7A6E5F] dark:text-[#B8A48C]';

        return (
          <React.Fragment key={index}>
            {item.href ? (
              <Link
                href={item.href}
                className={linkClassName || defaultLinkClassName}
                onClick={item.onClick}
              >
                {item.label}
              </Link>
            ) : (
              <span className={isLast ? lastSpanClassName : midSpanClassName}>
                {item.label}
              </span>
            )}
            {!isLast && (
              <ChevronIcon 
                className={chevronClassName || defaultChevronClassName} 
              />
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};
